import { Button, Flex, Input, Select } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useCallback } from 'react';

import AdminStudyTable from '@/features/admin/AdminStudyTable';
import { AdminStudyFilterRail } from '@/features/admin/AdminStudyFilterRail';
import UsersTable, { IUser } from '@/features/admin/UserTable';
import ReportSettings from '@/features/admin/ReportSettings';
import Integrations from '@/features/admin/Integrations';
import UserFeatureFlags from '@/features/admin/UserFeatureFlags';
import AuditTable from '@/features/admin/AuditTable';
import { AuditFilterRail } from '@/features/admin/AuditFilterRail';
import { AuditLogDrawer } from '@/features/admin/AuditLogDrawer';
import { formatTagLabel } from '@/shared/utils/tagLabel';
import {
  LayoutSubSidebar,
  LayoutSubHeader,
  LayoutPageTitle,
} from '@/features/layout';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/Tabs';
import { usePermissions } from '@/hooks/usePermissions';

import { useAdminState, type AdminTab } from './hooks/useAdminState';
import styles from './Admin.module.css';

const roleOptions = [
  { label: 'All Roles', value: 'All' },
  { label: 'Admin', value: 'Admin' },
  { label: 'Physician', value: 'Physician' },
  { label: 'Technician', value: 'Technician' },
];

const statusOptions = [
  { label: 'All Statuses', value: 'All' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Suspended', value: 'SUSPENDED' },
];

export default function Admin() {
  const {
    activeTab,
    onTabChange,
    studiesData,
    isFetchingStudies,
    studyFilters,
    usersData,
    isFetchingUsers,
    userParams,
    searchUserText,
    setSearchUserText,
    onUserTableChange,
    onRoleChange,
    onStatusChange,
    auditFilters,
    logItemsData,
    isFetchingLogItems,
    selectedLogItem,
    setSelectedLogItem,
  } = useAdminState();
  const isAdmin = usePermissions().role === 'ADMIN';

  const exportCsv = useCallback(() => {
    const head = [
      'Patient First',
      'Patient Last',
      'MRN',
      'Modality',
      'Priority',
      'Assigned',
      'Study Date',
      'Tags',
    ];
    const rows = (studiesData?.results || []).map((r) => [
      r.patientFirstName,
      r.patientLastName,
      r.patientMedicalRecordNumber,
      r.modality,
      r.priority,
      r.isAssigned ? 'Yes' : 'No',
      r.createdAt,
      (r.tags ?? []).map(formatTagLabel).join(', '),
    ]);
    const csv = [head, ...rows]
      .map((r) =>
        r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'studies.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [studiesData]);

  const renderFilters = () => {
    if (activeTab === 'studies') {
      const { params, onTableChange, ...filterProps } = studyFilters;
      return (
        <AdminStudyFilterRail
          {...filterProps}
          onExportCsv={exportCsv}
          isExportDisabled={isFetchingStudies}
        />
      );
    }

    if (activeTab === 'users') {
      return (
        <Flex vertical gap={12} className={styles.filterSection}>
          <div className={styles.filterGroup}>
            <label htmlFor="admin-search-users" className={styles.filterLabel}>
              Search
            </label>
            <Input
              id="admin-search-users"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Name or email"
              value={searchUserText}
              onChange={(e) => setSearchUserText(e.target.value)}
            />
          </div>

          <div className={styles.filterGroup}>
            <label htmlFor="admin-role" className={styles.filterLabel}>
              Role
            </label>
            <Select
              id="admin-role"
              defaultValue="All"
              onChange={(val) => onRoleChange(val as 'All' | IUser['role'])}
              options={roleOptions}
              className={styles.filterSelect}
            />
          </div>

          <div className={styles.filterGroup}>
            <label htmlFor="admin-status" className={styles.filterLabel}>
              Status
            </label>
            <Select
              id="admin-status"
              defaultValue="All"
              onChange={(val) => onStatusChange(val as 'All' | IUser['status'])}
              options={statusOptions}
              className={styles.filterSelect}
            />
          </div>

          <Button type="primary" icon={<PlusOutlined />}>
            Add User
          </Button>
        </Flex>
      );
    }

    if (activeTab === 'audit') {
      const { params, onTableChange, ...filterProps } = auditFilters;
      return <AuditFilterRail {...filterProps} />;
    }

    return null;
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'studies':
        return (
          <AdminStudyTable
            loading={isFetchingStudies}
            studies={studiesData}
            page={studyFilters.params.page!}
            pageSize={studyFilters.params.size!}
            onTableChange={studyFilters.onTableChange}
          />
        );
      case 'users':
        return (
          <UsersTable
            users={(usersData?.results ?? []) as unknown as IUser[]}
            page={userParams.page!}
            pageSize={userParams.size!}
            loading={isFetchingUsers}
            onTableChange={onUserTableChange}
            hideFilters
          />
        );
      case 'reporting-settings':
        return <ReportSettings />;
      case 'integrations':
        return <Integrations />;
      case 'feature-flags':
        return isAdmin ? <UserFeatureFlags /> : null;
      case 'audit':
        return (
          <AuditTable
            logItems={logItemsData?.results ?? []}
            total={logItemsData?.count}
            page={auditFilters.params.page!}
            pageSize={auditFilters.params.size!}
            loading={isFetchingLogItems}
            onTableChange={auditFilters.onTableChange}
            onRowClick={setSelectedLogItem}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <LayoutPageTitle title="Admin" />
      <LayoutSubSidebar title="Filters">{renderFilters()}</LayoutSubSidebar>

      <LayoutSubHeader>
        <Tabs
          value={activeTab}
          onValueChange={(v) => onTabChange(v as AdminTab)}
        >
          <TabsList>
            <TabsTrigger value="studies">Studies</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="reporting-settings">Reporting</TabsTrigger>
            <TabsTrigger value="integrations">Integrations</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="feature-flags">Feature Flags</TabsTrigger>
            )}
            <TabsTrigger value="audit">Audit</TabsTrigger>
          </TabsList>
        </Tabs>
      </LayoutSubHeader>

      <Flex vertical gap={16} className={styles.content}>
        {renderContent()}
      </Flex>

      <AuditLogDrawer
        logItem={selectedLogItem}
        onClose={() => setSelectedLogItem(null)}
      />
    </>
  );
}
